import { useState, useRef, useEffect } from "react";
import { ChatDots, X, PaperPlaneRight } from "@phosphor-icons/react";
import axios from "axios";
import Logo from "@/components/Logo";

const CHAT_API = "https://api.targetfunder.com/api/chat";

const WELCOME_MESSAGE = "Hi! I'm TargetFunder's help assistant. Ask me anything about the World Cup 2026 Predictor — whether you're a club looking to set up a fundraiser, or an entrant with questions about the competition.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: WELCOME_MESSAGE },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const payload = { message: userMsg };
      if (sessionId) payload.session_id = sessionId;
      const res = await axios.post(CHAT_API, payload);
      if (res.data.session_id) setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, { role: "bot", text: res.data.response }]);
    } catch {
      setMessages((prev) => [...prev, { role: "bot", text: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-4 w-[360px] sm:w-[380px] shadow-2xl flex flex-col overflow-hidden z-[9998]"
          style={{ height: "500px" }}
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-[#1D3557] px-4 py-3 flex items-center gap-3">
            <Logo size={24} />
            <div className="flex-1">
              <p className="font-heading font-bold text-sm text-white">TargetFunder Help</p>
              <p className="font-body text-[10px] text-[#A8DADC]">Powered by AI</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="chat-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-[#F8FAFA] p-4 space-y-3">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`max-w-[85%] ${msg.role === "user" ? "ml-auto" : ""}`}
              >
                <div
                  className={`px-3 py-2.5 font-body text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#1D3557] text-white rounded-t-lg rounded-bl-lg"
                      : "bg-white border border-[#1D3557]/8 text-[#1D3557]/80 rounded-t-lg rounded-br-lg"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="max-w-[85%]">
                <div className="bg-white border border-[#1D3557]/8 px-3 py-2.5 rounded-t-lg rounded-br-lg">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#1D3557]/30 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-[#1D3557]/30 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-[#1D3557]/30 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="bg-white border-t border-[#1D3557]/8 px-3 py-3 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              className="flex-1 border border-[#1D3557]/15 rounded-full px-4 py-2 font-body text-sm text-[#1D3557] placeholder:text-[#1D3557]/30 focus:outline-none focus:border-[#1D3557]/40"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-9 h-9 rounded-full bg-[#E63946] flex items-center justify-center hover:bg-[#d32f3c] transition-colors disabled:opacity-40"
              data-testid="chat-send-btn"
            >
              <PaperPlaneRight size={16} weight="fill" className="text-white" />
            </button>
          </form>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 w-14 h-14 rounded-full bg-[#1D3557] shadow-lg flex items-center justify-center hover:bg-[#152a47] transition-colors z-[9998]"
        data-testid="chat-toggle-btn"
      >
        {open ? (
          <X size={24} weight="bold" className="text-white" />
        ) : (
          <ChatDots size={24} weight="fill" className="text-white" />
        )}
      </button>
    </>
  );
}
